import ReactMarkdown from 'react-markdown';

const content = `# Vairo Privacy Policy

**Effective Date:** December 31, 2024

Vairo Inc. (“Vairo,” “we,” “us,” or “our”) is committed to respecting your privacy. This Privacy Policy explains how we collect, use, share, and otherwise process personal data (“Personal Data”) when you:

- Visit our websites that display or link to this Privacy Policy (collectively, the “Website”);
- Use our software, platform, and related services (collectively, the “Services”);
- Interact with us as a prospective customer, partner, vendor, or Authorized User;
- Participate in Vairo-hosted events, webinars, or campaigns;
- Engage with us in person or via phone, email, or other communication methods;
- Provide Personal Data to us through social media channels or on publicly available sites.

By using or accessing our Website or Services, you agree to the terms of this Privacy Policy. If you do not agree, you may not use our Website or Services.

## 1. Definitions

**Authorized User.** An individual who has been provided with access credentials or otherwise has access to use the Services under a Vairo customer agreement.

**Customer.** An individual or entity that purchases (or uses, if free) Vairo Services, under a separate agreement or terms of use.

**Personal Data.** Any information relating to an identified or identifiable natural person (e.g., name, email, billing information).

**Third-Party Services.** Products, applications, or services not provided by Vairo that may interoperate with the Services (e.g., a customer’s CRM or payment processor).

## 2. Personal Data We Collect

### 2.1 Information You Provide
- Account information (name, email, company, job title).
- Payment and billing information when purchasing Services.
- Customer-submitted data you upload to the platform (processed on your behalf).
- Support communications, event registrations, and other direct submissions.

### 2.2 Information Collected Automatically
- Usage data (IP address, browser type, pages visited, device type, timestamps).
- Cookies and tracking technologies for analytics and personalization.
- Diagnostic information (logs, error reports).

### 2.3 Information from Third Parties
- Data from integrations you authorize (e.g., CRM, accounting platforms).
- Publicly available sources and marketing providers.

## 3. How We Use Your Personal Data

We use Personal Data to:
- Provide and improve the Services.
- Authenticate accounts and maintain security.
- Respond to support requests and communicate updates.
- Personalize and analyze usage to improve user experience.
- Comply with legal obligations and enforce our policies.

## 4. How We Share Your Personal Data

We do not sell your Personal Data. We may share data:

- Within Vairo and our corporate affiliates for internal business purposes.
- With service providers who perform functions on our behalf (e.g., hosting, payments).
- In connection with business transactions (e.g., mergers, acquisitions).
- To comply with legal obligations or protect rights and safety.

## 5. International Transfers

We may process Personal Data in different jurisdictions. When we transfer data internationally, we use appropriate safeguards (e.g., contractual clauses) where required by law.

## 6. Data Security

We maintain administrative, technical, and physical safeguards to protect Personal Data. While we strive to protect data, no system is completely secure.

## 7. Data Retention

We retain Personal Data only as long as necessary to provide the Services, comply with legal obligations, or resolve disputes.

## 8. Your Rights and Choices

Depending on your jurisdiction, you may have rights to access, rectify, delete, or port your Personal Data, or to object to certain processing. To exercise rights, contact us at hello@vairo.ai.

## 9. Cookies and Tracking

We and our partners use cookies and similar technologies to operate and improve the Website and Services. You can manage cookie preferences through your browser or any on-site preference center.

## 10. Third-Party Sites

Our Website or Services may link to third-party sites; we are not responsible for their privacy practices. Please review their policies separately.

## 11. Children’s Privacy

Our Services are not directed to children under the age of 13. We do not knowingly collect Personal Data from children.

## 12. Changes to This Policy

We may update this Policy. If we make material changes, we will notify users and update the Effective Date.

## 13. Contact

Vairo Inc.
1 Greyrock Place, Stamford, CT 06901
Email: hello@vairo.ai
Website: https://www.vairo.ai
`;

function Privacy() {
  return (
    <div className="legal-content-wrapper">
      <article className="legal-content markdown">
        <ReactMarkdown>{content}</ReactMarkdown>
      </article>
    </div>
  );
}

export default Privacy;
